import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Handle, Position } from "@xyflow/react";

const buildHeaderRow = () => ({
  key: "",
  value: "",
});

const buildTestVarRow = () => ({
  name: "",
  value: "",
});

const DEFAULT_WEBHOOK_DATA = {
  method: "POST",
  url: "",
  headersEnabled: false,
  headers: [],
  bodyEnabled: false,
  requestBody: "",
  testEnabled: false,
  testVariables: [],
  testStatusCode: "",
  testResponseBody: "",
  saveResponseAsVariables: false,
  responseRoutingEnabled: false,
  expectedStatuses: ["200"],
};

export const WebhookMessageNode = ({ data, id, selected = false }) => {
  const safeData = { ...DEFAULT_WEBHOOK_DATA, ...(data || {}) };
  const [showMenu, setShowMenu] = useState(false);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const menuRef = useRef(null);

  const [method, setMethod] = useState("POST");
  const [url, setUrl] = useState(safeData.url || "");
  const [headersEnabled, setHeadersEnabled] = useState(Boolean(safeData.headersEnabled));
  const [headers, setHeaders] = useState(
    Array.isArray(safeData.headers) ? safeData.headers : []
  );
  const [bodyEnabled, setBodyEnabled] = useState(Boolean(safeData.bodyEnabled));
  const [requestBody, setRequestBody] = useState(safeData.requestBody || "");
  const [testEnabled, setTestEnabled] = useState(Boolean(safeData.testEnabled));
  const [testVariables, setTestVariables] = useState(
    Array.isArray(safeData.testVariables) ? safeData.testVariables : []
  );
  const [testStatusCode, setTestStatusCode] = useState(safeData.testStatusCode || "");
  const [testResponseBody, setTestResponseBody] = useState(safeData.testResponseBody || "");
  const [saveResponseAsVariables, setSaveResponseAsVariables] = useState(
    Boolean(safeData.saveResponseAsVariables)
  );
  const [responseRoutingEnabled, setResponseRoutingEnabled] = useState(
    Boolean(safeData.responseRoutingEnabled)
  );
  const [expectedStatuses, setExpectedStatuses] = useState(
    Array.isArray(safeData.expectedStatuses) && safeData.expectedStatuses.length > 0
      ? safeData.expectedStatuses
      : ["200"]
  );

  const [urlError, setUrlError] = useState("");
  const [requestBodyError, setRequestBodyError] = useState("");
  const [isTestingRequest, setIsTestingRequest] = useState(false);
  const [testRequestError, setTestRequestError] = useState("");

  const [editMethod, setEditMethod] = useState("POST");
  const [editUrl, setEditUrl] = useState(safeData.url || "");
  const [editHeadersEnabled, setEditHeadersEnabled] = useState(Boolean(safeData.headersEnabled));
  const [editHeaders, setEditHeaders] = useState(
    Array.isArray(safeData.headers) ? safeData.headers : []
  );
  const [editBodyEnabled, setEditBodyEnabled] = useState(Boolean(safeData.bodyEnabled));
  const [editRequestBody, setEditRequestBody] = useState(safeData.requestBody || "");
  const [editTestEnabled, setEditTestEnabled] = useState(Boolean(safeData.testEnabled));
  const [editTestVariables, setEditTestVariables] = useState(
    Array.isArray(safeData.testVariables) ? safeData.testVariables : []
  );
  const [editTestStatusCode, setEditTestStatusCode] = useState(safeData.testStatusCode || "");
  const [editTestResponseBody, setEditTestResponseBody] = useState(safeData.testResponseBody || "");
  const [editSaveResponseAsVariables, setEditSaveResponseAsVariables] = useState(
    Boolean(safeData.saveResponseAsVariables)
  );
  const [editResponseRoutingEnabled, setEditResponseRoutingEnabled] = useState(
    Boolean(safeData.responseRoutingEnabled)
  );
  const [editExpectedStatuses, setEditExpectedStatuses] = useState(
    Array.isArray(safeData.expectedStatuses) && safeData.expectedStatuses.length > 0
      ? safeData.expectedStatuses
      : ["200"]
  );

  useEffect(() => {
    setMethod("POST");
    setUrl(safeData.url || "");
    setHeadersEnabled(Boolean(safeData.headersEnabled));
    setHeaders(Array.isArray(safeData.headers) ? safeData.headers : []);
    setBodyEnabled(Boolean(safeData.bodyEnabled));
    setRequestBody(safeData.requestBody || "");
    setTestEnabled(Boolean(safeData.testEnabled));
    setTestVariables(Array.isArray(safeData.testVariables) ? safeData.testVariables : []);
    setTestStatusCode(safeData.testStatusCode || "");
    setTestResponseBody(safeData.testResponseBody || "");
    setSaveResponseAsVariables(Boolean(safeData.saveResponseAsVariables));
    setResponseRoutingEnabled(Boolean(safeData.responseRoutingEnabled));
    setExpectedStatuses(
      Array.isArray(safeData.expectedStatuses) && safeData.expectedStatuses.length > 0
        ? safeData.expectedStatuses
        : ["200"]
    );
  }, [
    safeData.url,
    safeData.headersEnabled,
    safeData.headers,
    safeData.bodyEnabled,
    safeData.requestBody,
    safeData.testEnabled,
    safeData.testVariables,
    safeData.testStatusCode,
    safeData.testResponseBody,
    safeData.saveResponseAsVariables,
    safeData.responseRoutingEnabled,
    safeData.expectedStatuses,
  ]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showMenu]);

  const isHandleConnected = useCallback(
    (handleId) => {
      if (!safeData.edges || !Array.isArray(safeData.edges)) return false;
      return safeData.edges.some((edge) => edge.sourceHandle === handleId);
    },
    [safeData.edges]
  );

  const validateUrl = (value) => {
    if (!value.trim()) {
      setUrlError("Webhook URL is required");
      return false;
    }
    if (!/^https?:\/\//i.test(value.trim())) {
      setUrlError("URL must start with http:// or https://");
      return false;
    }
    setUrlError("");
    return true;
  };

  const validateRequestBody = (value) => {
    if (!editBodyEnabled || !value.trim()) {
      setRequestBodyError("");
      return true;
    }
    try {
      JSON.parse(value);
      setRequestBodyError("");
      return true;
    } catch (_error) {
      setRequestBodyError("Request body must be valid JSON");
      return false;
    }
  };

  const syncToParent = (payload) => {
    if (safeData.onChangeMethod) safeData.onChangeMethod(id, "POST");
    if (safeData.onChangeUrl) safeData.onChangeUrl(id, payload.url);
    if (safeData.onChangeHeadersEnabled) {
      safeData.onChangeHeadersEnabled(id, payload.headersEnabled);
    }
    if (safeData.onChangeHeaders) safeData.onChangeHeaders(id, payload.headers);
    if (safeData.onChangeBodyEnabled) safeData.onChangeBodyEnabled(id, payload.bodyEnabled);
    if (safeData.onChangeRequestBody) safeData.onChangeRequestBody(id, payload.requestBody);
    if (safeData.onChangeTestEnabled) safeData.onChangeTestEnabled(id, payload.testEnabled);
    if (safeData.onChangeTestVariables) {
      safeData.onChangeTestVariables(id, payload.testVariables);
    }
    if (safeData.onChangeTestStatusCode) {
      safeData.onChangeTestStatusCode(id, payload.testStatusCode);
    }
    if (safeData.onChangeTestResponseBody) {
      safeData.onChangeTestResponseBody(id, payload.testResponseBody);
    }
    if (safeData.onChangeSaveResponseAsVariables) {
      safeData.onChangeSaveResponseAsVariables(id, payload.saveResponseAsVariables);
    }
    if (safeData.onChangeResponseRoutingEnabled) {
      safeData.onChangeResponseRoutingEnabled(id, payload.responseRoutingEnabled);
    }
    if (safeData.onChangeExpectedStatuses) {
      safeData.onChangeExpectedStatuses(id, payload.expectedStatuses);
    }
  };

  const handleEdit = () => {
    setEditMethod("POST");
    setEditUrl(url || "");
    setEditHeadersEnabled(headersEnabled);
    setEditHeaders(Array.isArray(headers) ? headers : []);
    setEditBodyEnabled(bodyEnabled);
    setEditRequestBody(requestBody || "");
    setEditTestEnabled(testEnabled);
    setEditTestVariables(Array.isArray(testVariables) ? testVariables : []);
    setEditTestStatusCode(testStatusCode || "");
    setEditTestResponseBody(testResponseBody || "");
    setEditSaveResponseAsVariables(saveResponseAsVariables);
    setEditResponseRoutingEnabled(responseRoutingEnabled);
    setEditExpectedStatuses(
      Array.isArray(expectedStatuses) && expectedStatuses.length > 0
        ? expectedStatuses
        : ["200"]
    );
    setUrlError("");
    setRequestBodyError("");
    setShowEditPopup(true);
    setShowMenu(false);
  };

  const handleSave = () => {
    const validUrl = validateUrl(editUrl);
    const validBody = validateRequestBody(editRequestBody);
    if (!validUrl || !validBody) return;

    const sanitizedHeaders = (editHeaders || [])
      .map((row) => ({ key: String(row.key || "").trim(), value: String(row.value || "").trim() }))
      .filter((row) => row.key || row.value);
    const sanitizedVars = (editTestVariables || [])
      .map((row) => ({
        name: String(row.name || "").trim(),
        value: String(row.value || "").trim(),
      }))
      .filter((row) => row.name || row.value);
    const sanitizedStatuses = (editExpectedStatuses || [])
      .map((item) => String(item || "").trim())
      .filter(Boolean);

    const payload = {
      method: "POST",
      url: editUrl.trim(),
      headersEnabled: editHeadersEnabled,
      headers: sanitizedHeaders,
      bodyEnabled: editBodyEnabled,
      requestBody: editRequestBody,
      testEnabled: editTestEnabled,
      testVariables: sanitizedVars,
      testStatusCode: editTestStatusCode.trim(),
      testResponseBody: editTestResponseBody,
      saveResponseAsVariables: editSaveResponseAsVariables,
      responseRoutingEnabled: editResponseRoutingEnabled,
      expectedStatuses: sanitizedStatuses.length > 0 ? sanitizedStatuses : ["200"],
    };

    syncToParent(payload);
    setMethod("POST");
    setUrl(payload.url);
    setHeadersEnabled(payload.headersEnabled);
    setHeaders(payload.headers);
    setBodyEnabled(payload.bodyEnabled);
    setRequestBody(payload.requestBody);
    setTestEnabled(payload.testEnabled);
    setTestVariables(payload.testVariables);
    setTestStatusCode(payload.testStatusCode);
    setTestResponseBody(payload.testResponseBody);
    setSaveResponseAsVariables(payload.saveResponseAsVariables);
    setResponseRoutingEnabled(payload.responseRoutingEnabled);
    setExpectedStatuses(payload.expectedStatuses);
    setShowEditPopup(false);
    setShowMenu(false);
  };

  const handleCancel = () => {
    setShowEditPopup(false);
    setShowMenu(false);
    setUrlError("");
    setRequestBodyError("");
    setTestRequestError("");
  };

  const handleDelete = () => {
    if (safeData.onDelete) safeData.onDelete(id);
    setShowMenu(false);
  };

  const addHeader = () => setEditHeaders([...(editHeaders || []), buildHeaderRow()]);
  const updateHeader = (index, field, value) => {
    const next = [...(editHeaders || [])];
    next[index] = { ...next[index], [field]: value };
    setEditHeaders(next);
  };
  const deleteHeader = (index) =>
    setEditHeaders((prev) => prev.filter((_, i) => i !== index));

  const addTestVariable = () =>
    setEditTestVariables([...(editTestVariables || []), buildTestVarRow()]);
  const updateTestVariable = (index, field, value) => {
    const next = [...(editTestVariables || [])];
    next[index] = { ...next[index], [field]: value };
    setEditTestVariables(next);
  };
  const deleteTestVariable = (index) =>
    setEditTestVariables((prev) => prev.filter((_, i) => i !== index));

  const addExpectedStatus = () => setEditExpectedStatuses([...(editExpectedStatuses || []), ""]);
  const updateExpectedStatus = (index, value) => {
    const next = [...(editExpectedStatuses || [])];
    next[index] = value.replace(/[^0-9]/g, "").slice(0, 3);
    setEditExpectedStatuses(next);
  };
  const deleteExpectedStatus = (index) =>
    setEditExpectedStatuses((prev) => prev.filter((_, i) => i !== index));

  const toggleStyle = (enabled) => ({
    width: "38px",
    height: "20px",
    borderRadius: "999px",
    border: "none",
    background: enabled ? "#2ecc71" : "#b5b5b5",
    position: "relative",
    cursor: "pointer",
    transition: "all 0.2s ease",
  });

  const knobStyle = (enabled) => ({
    width: "16px",
    height: "16px",
    borderRadius: "999px",
    background: "white",
    position: "absolute",
    top: "2px",
    left: enabled ? "20px" : "2px",
    transition: "all 0.2s ease",
  });

  const sectionToggle = (label, enabled, onToggle) => (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "12px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <strong style={{ fontSize: "26px", color: "#232323" }}>{label}</strong>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <span style={{ color: "#9b9b9b", fontStyle: "italic", fontSize: "20px" }}>
          Optional
        </span>
        <button type="button" onClick={onToggle} style={toggleStyle(enabled)}>
          <span style={knobStyle(enabled)} />
        </button>
      </div>
    </div>
  );

  const buildVariableMap = () => {
    const entries = (editTestVariables || []).map((row) => [
      String(row?.name || "").trim(),
      String(row?.value || ""),
    ]);
    return Object.fromEntries(entries.filter(([k]) => Boolean(k)));
  };

  const applyVariables = (value, variableMap) => {
    if (!value) return "";
    return String(value).replace(/@([a-zA-Z_][a-zA-Z0-9_]*)/g, (_match, name) => {
      if (Object.prototype.hasOwnProperty.call(variableMap, name)) {
        return variableMap[name];
      }
      return `@${name}`;
    });
  };

  const handleTestRequest = async () => {
    setTestRequestError("");
    const validUrl = validateUrl(editUrl);
    const validBody = validateRequestBody(editRequestBody);
    if (!validUrl || !validBody) return;

    const variableMap = buildVariableMap();
    const resolvedUrl = applyVariables(editUrl.trim(), variableMap);
    const resolvedHeaders = (editHeaders || [])
      .map((row) => ({
        key: applyVariables(String(row?.key || "").trim(), variableMap),
        value: applyVariables(String(row?.value || "").trim(), variableMap),
      }))
      .filter((row) => row.key);

    let requestBodyToSend;
    if (editBodyEnabled && editRequestBody.trim()) {
      try {
        const bodyWithVariables = applyVariables(editRequestBody, variableMap);
        requestBodyToSend = JSON.stringify(JSON.parse(bodyWithVariables));
      } catch (_error) {
        setRequestBodyError("Request body must be valid JSON");
        return;
      }
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);
    setIsTestingRequest(true);

    try {
      const response = await fetch(resolvedUrl, {
        method: "POST",
        headers: Object.fromEntries(resolvedHeaders.map((h) => [h.key, h.value])),
        body: requestBodyToSend,
        signal: controller.signal,
      });

      const responseText = await response.text();
      let prettyBody = responseText;
      try {
        const parsed = JSON.parse(responseText);
        prettyBody = JSON.stringify(parsed, null, 2);
      } catch (_ignore) {
        // Keep plain text response when not JSON
      }

      setEditTestStatusCode(String(response.status));
      setEditTestResponseBody(prettyBody);
    } catch (error) {
      const message =
        error?.name === "AbortError"
          ? "Request timed out after 15 seconds"
          : error?.message || "Request failed";
      setEditTestStatusCode("0");
      setEditTestResponseBody(message);
      setTestRequestError(message);
    } finally {
      clearTimeout(timeoutId);
      setIsTestingRequest(false);
    }
  };

  const normalizedExpectedStatuses = (editExpectedStatuses || [])
    .map((status) => String(status || "").trim())
    .filter(Boolean);
  const hasTestStatusCode = String(editTestStatusCode || "").trim().length > 0;
  const testStatusMatches =
    hasTestStatusCode && normalizedExpectedStatuses.includes(String(editTestStatusCode).trim());

  const mainHandleId = `${id}-main`;
  const connected = isHandleConnected(mainHandleId);

  return (
    <>
      <div
        style={{
          background: selected ? "#f8f9fa" : "#f1f3f4",
          border: `1px solid ${selected ? "#1976d2" : "#e0e0e0"}`,
          borderRadius: "6px",
          padding: "8px",
          width: "180px",
          minHeight: "70px",
          boxShadow: selected
            ? "0 4px 8px rgba(25, 118, 210, 0.3)"
            : "0 2px 4px rgba(0,0,0,0.1)",
          position: "relative",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 10,
            right: 8,
            cursor: "pointer",
            fontSize: 16,
            color: selected ? "#1976d2" : "#6c757d",
            zIndex: 1,
            userSelect: "none",
            fontWeight: "bold",
            lineHeight: "1",
            display: "flex",
            alignItems: "center",
            height: "11px",
          }}
          onClick={() => setShowMenu(!showMenu)}
          title="Menu"
        >
          ⋮
        </div>

        {showMenu && (
          <div
            ref={menuRef}
            style={{
              position: "absolute",
              top: 30,
              right: 8,
              background: "white",
              border: "1px solid #ddd",
              borderRadius: "4px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              zIndex: 2,
              minWidth: "100px",
            }}
          >
            <div
              onClick={handleEdit}
              style={{ padding: "8px 12px", cursor: "pointer", fontSize: "12px" }}
            >
              ✏️ Edit
            </div>
            <div
              onClick={handleDelete}
              style={{
                padding: "8px 12px",
                cursor: "pointer",
                fontSize: "12px",
                borderTop: "1px solid #eee",
              }}
            >
              🗑️ Delete
            </div>
          </div>
        )}

        <Handle
          type="target"
          position={Position.Left}
          style={{ background: selected ? "#1976d2" : "#6c757d" }}
        />
        <Handle
          type="source"
          position={Position.Right}
          id={mainHandleId}
          style={{
            background: connected ? "#28a745" : selected ? "#1976d2" : "#6c757d",
            width: "8px",
            height: "8px",
            right: "-4px",
            top: "50%",
            transform: "translateY(-50%)",
            border: "2px solid white",
            opacity: connected ? 0.7 : 1,
          }}
        />

        <div style={{ textAlign: "left" }}>
          <div
            style={{
              fontWeight: "bold",
              color: selected ? "#1976d2" : "#6c757d",
              marginBottom: "6px",
              fontSize: "11px",
            }}
          >
            Webhook
          </div>
          <div style={{ fontSize: "11px", color: "#198754", marginBottom: "4px" }}>
            POST
          </div>
          <div
            style={{
              fontSize: "12px",
              color: url ? "#444" : "#999",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontStyle: url ? "normal" : "italic",
            }}
            title={url || "Click Edit to set URL"}
          >
            {url || "Click Edit to set URL"}
          </div>
        </div>
      </div>

      {showEditPopup &&
        createPortal(
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 9999,
            }}
            onClick={handleCancel}
          >
            <div
              style={{
                background: "white",
                borderRadius: "8px",
                width: "680px",
                maxHeight: "86vh",
                overflowY: "auto",
                boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "18px 24px",
                  borderBottom: "1px solid #ececec",
                  position: "sticky",
                  top: 0,
                  backgroundColor: "white",
                  zIndex: 1,
                }}
              >
                <h3 style={{ margin: 0, fontSize: "28px", color: "#222" }}>Webhook</h3>
                <button
                  type="button"
                  onClick={handleCancel}
                  style={{
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    fontSize: "26px",
                    color: "#333",
                    lineHeight: 1,
                  }}
                >
                  ×
                </button>
              </div>

              <div style={{ padding: "22px 24px 28px 24px" }}>
                <div style={{ marginBottom: "18px" }}>
                  <div
                    style={{
                      fontSize: "30px",
                      fontWeight: 700,
                      marginBottom: "12px",
                      color: "#232323",
                    }}
                  >
                    URL &amp; Method
                  </div>
                  <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                    <select
                      value={editMethod}
                      onChange={() => setEditMethod("POST")}
                      style={{
                        width: "95px",
                        padding: "10px",
                        border: "1px solid #e3e6ef",
                        borderRadius: "8px",
                        fontSize: "14px",
                        backgroundColor: "#f8f9fd",
                      }}
                    >
                      <option value="POST">POST</option>
                    </select>
                    <input
                      type="text"
                      value={editUrl}
                      onChange={(e) => {
                        setEditUrl(e.target.value);
                        validateUrl(e.target.value);
                      }}
                      placeholder="https://"
                      style={{
                        flex: 1,
                        padding: "10px 12px",
                        border: "1px solid #e3e6ef",
                        borderRadius: "8px",
                        fontSize: "14px",
                      }}
                    />
                  </div>
                  {urlError && (
                    <div style={{ marginTop: "6px", color: "#dc3545", fontSize: "12px" }}>
                      {urlError}
                    </div>
                  )}
                </div>

                <hr style={{ border: 0, borderTop: "1px solid #efefef", margin: "18px 0" }} />
                {sectionToggle("Customize Headers", editHeadersEnabled, () =>
                  setEditHeadersEnabled((prev) => !prev)
                )}
                <div style={{ color: "#666", marginBottom: "8px", fontSize: "14px" }}>
                  Add headers to your request (example: Content-Type: application/json)
                </div>
                <div style={{ color: "#f39c12", marginBottom: "12px", fontSize: "14px" }}>
                  (User-Agent is not sent as a header by default. make sure you include it if necessary.)
                </div>
                {editHeadersEnabled && (
                  <div style={{ marginBottom: "10px" }}>
                    <div style={{ fontWeight: 700, marginBottom: "8px" }}>Edit Headers</div>
                    {(editHeaders || []).map((row, index) => (
                      <div
                        key={`hdr-${index}`}
                        style={{ display: "flex", gap: "10px", alignItems: "center", marginBottom: "10px" }}
                      >
                        <input
                          type="text"
                          value={row.key || ""}
                          onChange={(e) => updateHeader(index, "key", e.target.value)}
                          placeholder="Key"
                          style={{
                            flex: 1,
                            padding: "8px",
                            border: "1px solid #ececec",
                            borderRadius: "6px",
                          }}
                        />
                        <input
                          type="text"
                          value={row.value || ""}
                          onChange={(e) => updateHeader(index, "value", e.target.value)}
                          placeholder="Value"
                          style={{
                            flex: 1,
                            padding: "8px",
                            border: "1px solid #ececec",
                            borderRadius: "6px",
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => deleteHeader(index)}
                          style={{
                            border: "none",
                            background: "transparent",
                            color: "#dc3545",
                            cursor: "pointer",
                            fontSize: "18px",
                          }}
                        >
                          🗑
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addHeader}
                      style={{
                        width: "100%",
                        border: "1px dashed #25b26b",
                        background: "white",
                        borderRadius: "8px",
                        padding: "10px",
                        color: "#25b26b",
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                    >
                      Add Header
                    </button>
                  </div>
                )}

                <hr style={{ border: 0, borderTop: "1px solid #efefef", margin: "18px 0" }} />
                {sectionToggle("Customize Body", editBodyEnabled, () =>
                  setEditBodyEnabled((prev) => !prev)
                )}
                <div style={{ color: "#f39c12", marginBottom: "8px", fontSize: "14px" }}>
                  Formatting Tips:
                </div>
                <div style={{ color: "#f39c12", marginBottom: "8px", fontSize: "14px" }}>
                  - Strings and Variables must be wrapped in quotes {'"@name"'}
                </div>
                <div style={{ color: "#f39c12", marginBottom: "12px", fontSize: "14px" }}>
                  - A comma is needed between 2 key-value pairs {'"Email": "@email", "Name": "@name"'}
                </div>
                {editBodyEnabled && (
                  <div style={{ marginBottom: "10px" }}>
                    <div style={{ fontWeight: 700, marginBottom: "8px" }}>
                      Request Body (JSON only)
                    </div>
                    <textarea
                      value={editRequestBody}
                      onChange={(e) => {
                        setEditRequestBody(e.target.value);
                        validateRequestBody(e.target.value);
                      }}
                      rows={6}
                      style={{
                        width: "100%",
                        border: "1px solid #ececec",
                        borderRadius: "6px",
                        padding: "10px",
                        fontFamily: "monospace",
                        fontSize: "13px",
                        resize: "vertical",
                      }}
                    />
                    {requestBodyError && (
                      <div style={{ marginTop: "6px", color: "#dc3545", fontSize: "12px" }}>
                        {requestBodyError}
                      </div>
                    )}
                  </div>
                )}

                <hr style={{ border: 0, borderTop: "1px solid #efefef", margin: "18px 0" }} />
                {sectionToggle("Test Your Request", editTestEnabled, () =>
                  setEditTestEnabled((prev) => !prev)
                )}
                <div style={{ color: "#666", marginBottom: "8px", fontSize: "14px" }}>
                  Manually set values for test variables
                </div>
                <div style={{ color: "#f39c12", marginBottom: "12px", fontSize: "14px" }}>
                  (If your request contains variables, you can manually set their values for testing purposes.)
                </div>
                {editTestEnabled && (
                  <>
                    <div style={{ marginBottom: "10px" }}>
                      <div style={{ fontWeight: 700, marginBottom: "8px" }}>Edit Test Variables</div>
                      {(editTestVariables || []).map((row, index) => (
                        <div
                          key={`tv-${index}`}
                          style={{ display: "flex", gap: "10px", alignItems: "center", marginBottom: "10px" }}
                        >
                          <input
                            type="text"
                            value={row.name || ""}
                            onChange={(e) => updateTestVariable(index, "name", e.target.value)}
                            placeholder="Variable Name"
                            style={{
                              flex: 1,
                              padding: "8px",
                              border: "1px solid #ececec",
                              borderRadius: "6px",
                            }}
                          />
                          <input
                            type="text"
                            value={row.value || ""}
                            onChange={(e) => updateTestVariable(index, "value", e.target.value)}
                            placeholder="Test Value"
                            style={{
                              flex: 1,
                              padding: "8px",
                              border: "1px solid #ececec",
                              borderRadius: "6px",
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => deleteTestVariable(index)}
                            style={{
                              border: "none",
                              background: "transparent",
                              color: "#dc3545",
                              cursor: "pointer",
                              fontSize: "18px",
                            }}
                          >
                            🗑
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={addTestVariable}
                        style={{
                          width: "100%",
                          border: "1px dashed #25b26b",
                          background: "white",
                          borderRadius: "8px",
                          padding: "10px",
                          color: "#25b26b",
                          fontWeight: 600,
                          cursor: "pointer",
                        }}
                      >
                        Add Test Variable
                      </button>
                    </div>

                    <button
                      type="button"
                      onClick={handleTestRequest}
                      disabled={isTestingRequest}
                      style={{
                        width: "100%",
                        border: "none",
                        borderRadius: "8px",
                        padding: "11px",
                        backgroundColor: isTestingRequest ? "#8bcfaa" : "#18a85d",
                        color: "white",
                        fontWeight: 700,
                        cursor: isTestingRequest ? "not-allowed" : "pointer",
                        marginBottom: "14px",
                      }}
                    >
                      {isTestingRequest ? "Testing..." : "Test the request"}
                    </button>
                    {testRequestError && (
                      <div style={{ color: "#dc3545", fontSize: "12px", marginBottom: "12px" }}>
                        {testRequestError}
                      </div>
                    )}

                    <div style={{ marginBottom: "10px" }}>
                      <div style={{ fontWeight: 700, marginBottom: "6px" }}>Test Status Code</div>
                      <input
                        type="text"
                        value={editTestStatusCode}
                        onChange={(e) =>
                          setEditTestStatusCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 3))
                        }
                        placeholder="400"
                        style={{
                          width: "100%",
                          padding: "8px",
                          border: "1px solid #ececec",
                          borderRadius: "6px",
                        }}
                      />
                      {hasTestStatusCode && (
                        <div
                          style={{
                            marginTop: "6px",
                            fontSize: "12px",
                            color: testStatusMatches ? "#198754" : "#dc3545",
                            fontWeight: 600,
                          }}
                        >
                          {testStatusMatches
                            ? "Matches Expected Statuses for response routing."
                            : "Does not match current Expected Statuses."}
                        </div>
                      )}
                    </div>
                    <div style={{ marginBottom: "10px" }}>
                      <div style={{ fontWeight: 700, marginBottom: "6px" }}>Test Status Code</div>
                      <textarea
                        value={editTestResponseBody}
                        onChange={(e) => setEditTestResponseBody(e.target.value)}
                        rows={3}
                        style={{
                          width: "100%",
                          padding: "8px",
                          border: "1px solid #ececec",
                          borderRadius: "6px",
                        }}
                      />
                    </div>
                  </>
                )}

                <hr style={{ border: 0, borderTop: "1px solid #efefef", margin: "18px 0" }} />
                {sectionToggle(
                  "Save Responses as Variables",
                  editSaveResponseAsVariables,
                  () => setEditSaveResponseAsVariables((prev) => !prev)
                )}

                <hr style={{ border: 0, borderTop: "1px solid #efefef", margin: "18px 0" }} />
                {sectionToggle("Response Routing", editResponseRoutingEnabled, () =>
                  setEditResponseRoutingEnabled((prev) => !prev)
                )}
                <div style={{ color: "#666", marginBottom: "8px", fontSize: "14px" }}>
                  Split your chatbot based on response status codes (200, 400, 500, etc).
                </div>
                {editResponseRoutingEnabled && (
                  <>
                    <div style={{ fontWeight: 700, marginBottom: "8px" }}>Expected Statuses</div>
                    {(editExpectedStatuses || []).map((status, index) => (
                      <div
                        key={`status-${index}`}
                        style={{ display: "flex", gap: "10px", alignItems: "center", marginBottom: "10px" }}
                      >
                        <input
                          type="text"
                          value={status}
                          onChange={(e) => updateExpectedStatus(index, e.target.value)}
                          placeholder="200"
                          style={{
                            flex: 1,
                            padding: "8px",
                            border: "1px solid #ececec",
                            borderRadius: "6px",
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => deleteExpectedStatus(index)}
                          style={{
                            border: "none",
                            background: "transparent",
                            color: "#dc3545",
                            cursor: "pointer",
                            fontSize: "18px",
                          }}
                        >
                          🗑
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addExpectedStatus}
                      style={{
                        width: "100%",
                        border: "1px dashed #25b26b",
                        background: "white",
                        borderRadius: "8px",
                        padding: "10px",
                        color: "#25b26b",
                        fontWeight: 600,
                        cursor: "pointer",
                        marginBottom: "12px",
                      }}
                    >
                      Add Expected Status
                    </button>
                    {hasTestStatusCode && (
                      <div
                        style={{
                          fontSize: "12px",
                          color: testStatusMatches ? "#198754" : "#dc3545",
                          marginBottom: "10px",
                        }}
                      >
                        Last test status `{editTestStatusCode}`{" "}
                        {testStatusMatches ? "is routable." : "is not routable yet."}
                      </div>
                    )}
                  </>
                )}

                <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px", marginTop: "14px" }}>
                  <button
                    type="button"
                    onClick={handleCancel}
                    style={{
                      padding: "8px 22px",
                      borderRadius: "8px",
                      border: "1px solid #1ba260",
                      backgroundColor: "white",
                      color: "#1ba260",
                      cursor: "pointer",
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    style={{
                      padding: "8px 22px",
                      borderRadius: "8px",
                      border: "1px solid #1ba260",
                      backgroundColor: "#1ba260",
                      color: "white",
                      cursor: "pointer",
                      fontWeight: 700,
                    }}
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

